/**
 * Main JavaScript entry point
 *
 * This imports all necessary modules and styles
 */

// Import main stylesheet
import "../scss/style.scss";

// Import Lenis for smooth scrolling
import Lenis from "@studio-freight/lenis";

// Import GSAP for ScrollTrigger integration
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import custom modules
import initNavScroll from "./modules/nav-scroll";
import initMobileNav from "./modules/mobile-nav";
import initSliders from "./modules/sliders";
import initVideoModal from "./modules/video-modal";
import initGlobalModal from "./modules/modal";

// DOM ready
document.addEventListener("DOMContentLoaded", function () {
  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);

  // Initialize Lenis smooth scrolling
  const lenis = initLenis();
  // Make lenis available globally for other modules
  window.lenis = lenis;

  // Configure ScrollTrigger to work with Lenis
  ScrollTrigger.scrollerProxy(document.body, {
    scrollTop(value) {
      return arguments.length
        ? lenis.scrollTo(value, 0, 0)
        : lenis.animatedScroll;
    },
    getBoundingClientRect() {
      return {
        top: 0,
        left: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      };
    },
    pinType: document.body.style.transform ? "transform" : "fixed",
  });

  // Initialize modules
  initNavScroll();
  initMobileNav();
  initSliders();
  initVideoModal();
  initGlobalModal();

  // Refresh ScrollTrigger after all animations are initialized
  ScrollTrigger.refresh();

  setTimeout(() => {
    if (window.location.hash && window.scrollToElement) {
      window.scrollToElement(window.location.hash);
    }
  }, 100);

  // Update ScrollTrigger on window resize
  window.addEventListener("resize", () => {
    ScrollTrigger.refresh();
  });
});

/**
 * Initialize Lenis smooth scrolling with pinned section support
 */
function initLenis() {
  // Initialize Lenis with options
  const lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    direction: "vertical",
    gestureDirection: "vertical",
    smooth: true,
    mouseMultiplier: 1,
    smoothTouch: false,
    touchMultiplier: 2,
    infinite: false,
  });

  /**
   * Smart scroll to element that handles pinned sections
   */
  function scrollToElement(targetSelector, options = {}) {
    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;

    // Special handling for kitchen gallery sections
    if (targetSelector.includes("kitchen-section-gallery")) {
      // Find the ScrollTrigger for this specific gallery element
      const galleryScrollTrigger = ScrollTrigger.getAll().find((st) => {
        return st.trigger === targetElement && st.start === "top top";
      });

      if (galleryScrollTrigger) {
        // Scroll to where this gallery would trigger (when it reaches top of viewport)
        const scrollPosition = galleryScrollTrigger.start;
        lenis.scrollTo(scrollPosition, {
          offset: options.offset || 0,
          duration: options.duration || 1.2,
        });
        return;
      }
    }

    // Check if the target element is part of a pinned ScrollTrigger
    const pinnedScrollTrigger = ScrollTrigger.getAll().find((st) => {
      // Check if this ScrollTrigger pins the target element or its parent
      if (
        st.pin &&
        (st.trigger === targetElement || st.trigger.contains(targetElement))
      ) {
        return true;
      }
      return false;
    });

    if (pinnedScrollTrigger) {
      // For pinned sections, scroll to the trigger start position
      const scrollPosition = pinnedScrollTrigger.start;
      lenis.scrollTo(scrollPosition, {
        offset: options.offset || 0,
        duration: options.duration || 1.2,
      });
    } else {
      // For non-pinned sections, use regular scroll
      lenis.scrollTo(targetSelector, {
        offset: options.offset || 0,
        duration: options.duration || 1.2,
      });
    }
  }

  /**
   * Alternative approach: Scroll to pinned section by progress
   */
  function scrollToPinnedSection(targetSelector, progress = 0, options = {}) {
    const targetElement = document.querySelector(targetSelector);
    if (!targetElement) return;

    const pinnedScrollTrigger = ScrollTrigger.getAll().find((st) => {
      return (
        st.pin &&
        (st.trigger === targetElement || st.trigger.contains(targetElement))
      );
    });

    if (pinnedScrollTrigger) {
      // Calculate position based on progress through the pin duration
      const pinStart = pinnedScrollTrigger.start;
      const pinEnd = pinnedScrollTrigger.end;
      const scrollPosition = pinStart + (pinEnd - pinStart) * progress;

      lenis.scrollTo(scrollPosition, {
        offset: options.offset || 0,
        duration: options.duration || 1.2,
      });
    } else {
      // Fallback to regular scroll
      scrollToElement(targetSelector, options);
    }
  }

  // Handle hash in URL on page load (for cross-page navigation)
  function handleInitialHash() {
    if (window.location.hash) {
      // For kitchen gallery sections, wait longer to ensure ScrollTriggers are set up
      const isKitchenGallery = window.location.hash.includes(
        "kitchen-section-gallery"
      );
      const delay = isKitchenGallery ? 500 : 200;

      // Wait for ScrollTrigger to initialize all pins and triggers
      setTimeout(() => {
        scrollToElement(window.location.hash, {
          offset: 0, // Adjust if you have a fixed header
          duration: 1.2,
        });
      }, delay);

      // Also listen for ScrollTrigger refresh in case it happens after our timeout
      const onRefresh = () => {
        setTimeout(() => {
          scrollToElement(window.location.hash, {
            offset: 0,
            duration: 1.2,
          });
        }, 100);
        ScrollTrigger.removeEventListener("refresh", onRefresh);
      };

      ScrollTrigger.addEventListener("refresh", onRefresh);
    }
  }

  // Get all links that hash (same-page navigation)
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener("click", function (e) {
      e.preventDefault();

      const targetId = this.getAttribute("href");
      if (targetId === "#") return;

      // Check if the link has a data attribute for pin progress
      const pinProgress = this.dataset.pinProgress
        ? parseFloat(this.dataset.pinProgress)
        : 0;

      if (pinProgress > 0) {
        scrollToPinnedSection(targetId, pinProgress);
      } else {
        scrollToElement(targetId);
      }
    });
  });

  // Handle browser back/forward with hash changes
  window.addEventListener("hashchange", function () {
    if (window.location.hash) {
      setTimeout(() => {
        scrollToElement(window.location.hash, {
          offset: 0,
          duration: 1.2,
        });
      }, 50);
    }
  });

  // Connect lenis to RAF (request animation frame)
  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }

  requestAnimationFrame(raf);

  // Connect lenis to scroll events to update scroll trigger animations
  lenis.on("scroll", ScrollTrigger.update);

  // Add data attributes to body for potential styling during smooth scrolling
  function lenisStyles() {
    if (lenis.isStopped) {
      document.body.setAttribute("data-lenis-stopped", "");
    } else {
      document.body.removeAttribute("data-lenis-stopped");
    }

    if (lenis.isScrolling) {
      document.body.setAttribute("data-lenis-scrolling", "");
    } else {
      document.body.removeAttribute("data-lenis-scrolling");
    }

    if (lenis.direction === 1) {
      document.body.setAttribute("data-lenis-scrolling-down", "");
      document.body.removeAttribute("data-lenis-scrolling-up");
    } else if (lenis.direction === -1) {
      document.body.setAttribute("data-lenis-scrolling-up", "");
      document.body.removeAttribute("data-lenis-scrolling-down");
    }
  }

  lenis.on("scroll", lenisStyles);

  // Handle initial hash after ScrollTrigger is ready
  handleInitialHash();

  // Make scrollToElement available globally for other modules
  window.scrollToElement = scrollToElement;
  window.scrollToPinnedSection = scrollToPinnedSection;

  return lenis;
}
